import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="mercadopago-checkout"
// Loads MercadoPago SDK dynamically and handles card tokenization
export default class extends Controller {
  static values = {
    publicKey: String,
    locale: { type: String, default: "es-MX" },
    processingText: { type: String, default: "Processing..." },
    completeText: { type: String, default: "Complete Purchase" }
  }

  static targets = [
    "form", "submitButton", "errors", "cardToken",
    "cardNumber", "cardExpirationMonth", "cardExpirationYear",
    "securityCode", "cardholderName", "docType", "docNumber"
  ]

  connect() {
    this.loadMercadoPago()
  }

  async loadMercadoPago() {
    if (typeof MercadoPago !== "undefined") {
      this.initializeMercadoPago()
      return
    }

    const script = document.createElement("script")
    script.src = "https://sdk.mercadopago.com/js/v2"
    script.onload = () => this.initializeMercadoPago()
    document.head.appendChild(script)
  }

  initializeMercadoPago() {
    this.mp = new MercadoPago(this.publicKeyValue, {
      locale: this.localeValue
    })

    this.mp.getIdentificationTypes().then(response => {
      response.forEach(type => {
        const option = document.createElement("option")
        option.value = type.id
        option.textContent = type.name
        this.docTypeTarget.appendChild(option)
      })

      const rfcOption = response.find(type => type.id === "RFC")
      if (rfcOption) {
        this.docTypeTarget.value = "RFC"
      }
    })

    this.currentPaymentMethod = null
  }

  detectCardBrand() {
    const bin = this.cardNumberTarget.value.replace(/\s/g, "").substring(0, 6)

    if (bin.length >= 6) {
      this.mp.getPaymentMethods({ bin: bin }).then(response => {
        if (response.results && response.results.length > 0) {
          this.currentPaymentMethod = response.results[0]
          this.securityCodeTarget.maxLength = this.currentPaymentMethod.security_code.length
        }
      })
    }
  }

  async submit(event) {
    // Check if using saved payment method
    const useSavedMethod = document.querySelector('input[name="purchase[use_saved_payment_method]"]:checked')
    if (useSavedMethod && useSavedMethod.value === "1") {
      return true
    }

    event.preventDefault()

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.value = this.processingTextValue
    this.hideError()

    const cardData = {
      cardNumber: this.cardNumberTarget.value.replace(/\s/g, ""),
      cardholderName: this.cardholderNameTarget.value,
      cardExpirationMonth: this.cardExpirationMonthTarget.value,
      cardExpirationYear: "20" + this.cardExpirationYearTarget.value,
      securityCode: this.securityCodeTarget.value,
      identificationType: this.docTypeTarget.value,
      identificationNumber: this.docNumberTarget.value
    }

    if (!cardData.cardNumber || !cardData.cardholderName ||
        !cardData.cardExpirationMonth || !cardData.cardExpirationYear ||
        !cardData.securityCode || !cardData.identificationType ||
        !cardData.identificationNumber) {
      this.showError("Please fill in all card details")
      this.resetButton()
      return
    }

    try {
      const token = await this.mp.createCardToken(cardData)

      if (token.id) {
        this.cardTokenTarget.value = token.id
        this.formTarget.submit()
      } else {
        throw new Error("Failed to create card token")
      }
    } catch (error) {
      console.error("MercadoPago tokenization error:", error)

      let errorMessage = "An error occurred processing your card. Please check your details and try again."

      if (error.cause && error.cause.length > 0) {
        const cause = error.cause[0]
        errorMessage = this.mapErrorCode(cause.code, cause.description) || errorMessage
      }

      this.showError(errorMessage)
      this.resetButton()
    }
  }

  mapErrorCode(code, description) {
    const errorMap = {
      "205": "Please enter a valid card number",
      "208": "Please select an expiration month",
      "209": "Please select an expiration year",
      "212": "Please enter a valid document type",
      "213": "Please enter a valid document number",
      "214": "Please enter a valid document number",
      "220": "Please enter a valid document type",
      "221": "Please enter the cardholder name",
      "224": "Please enter a valid security code",
      "E301": "Please enter a valid card number",
      "E302": "Please check your security code",
      "316": "Please enter a valid cardholder name",
      "322": "Please check your document type",
      "323": "Please check your document number",
      "324": "Please check your document number",
      "325": "Please check your expiration date",
      "326": "Please check your expiration date"
    }
    return errorMap[code] || description
  }

  resetButton() {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.value = this.completeTextValue
  }

  showError(message) {
    if (!this.hasErrorsTarget) return
    const errorMessage = this.errorsTarget.querySelector("p")
    errorMessage.textContent = message
    this.errorsTarget.classList.remove("hidden")
    this.errorsTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  hideError() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("hidden")
  }
}
